import React, { useCallback, useMemo } from 'react';

import { useScopedPersistedSelection } from '@src/corelib/hooks';

const LocationContext = React.createContext(null);

const STORAGE_PREFIX = 'ao-location:';


/** Holds the active inventory-location filter, scoped per seller and persisted
 * in localStorage. "all" means no `location` query param is sent (every yard).
 * The selector lives in the Dashboard / Inventory / Listings top bars and the
 * chosen id is threaded into those queries (and the parts export) so the views
 * scope together (#170). */
function LocationProvider({ sellerId, children }) {
  // Active location id, persisted per seller ("all" = every yard); re-reads when
  // the seller changes.
  const [locationId, selectLocation] = useScopedPersistedSelection(STORAGE_PREFIX, sellerId, 'all');

  // Normalise a falsy pick back to "all" (the "omit the param" sentinel).
  const setLocationId = useCallback((nextId) => selectLocation(nextId || 'all'), [selectLocation]);

  const contextValue = useMemo(() => ({
    locationId,
    // Convenience for query builders: undefined when "all" (omit the param).
    locationParam: locationId && locationId !== 'all' ? locationId : undefined,
    setLocationId,
  }), [locationId, setLocationId]);

  return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>;
}

LocationProvider.displayName = 'LocationProvider';
export { LocationContext, LocationProvider };
